/**
 * GRID CONTINUITY — when did the site lose the grid, for how long, and what carried it (Hamzah,
 * 2026-10-16: "do the rest", after "when was the grid last lost" was proposed as the question a
 * bank asks first). The arithmetic behind the Overview's Grid continuity card and the Backup tab's.
 *
 * TWO WITNESSES OF THE GRID, because one of them goes dark exactly when it matters:
 *
 *   the grid incomers' meters   their line voltages (`line_to_line_volts`). A meter is often
 *                               powered from the supply it measures: in a real outage it may stop
 *                               reporting rather than read 0 V;
 *   the generator controller    its own view of the mains (`mains_phase_voltages`), which it reads
 *                               to decide whether to start. It runs from the generator's starter
 *                               battery, so it keeps reporting through an outage.
 *
 * A bucket is a GRID LOSS only when every witness that reported in it saw a dead reading (under
 * half the nominal voltage) — so one meter glitching to 0 V while the controller still sees the
 * mains is not a loss. A bucket no witness reported in is NOT READ, never a loss and never grid:
 * the platform outage of Oct 15 must not be counted as either. The generator is CARRYING the site
 * when its output is above the idle floor; a run with no load (a test off load) leaves the state
 * at grid.
 *
 * Readings are BUCKET MINIMA for the voltages (any dead reading in the bucket counts, so a short
 * loss inside a quarter hour is not averaged away) and BUCKET MAXIMA for the generator's output.
 * Durations are therefore to the bucket: a loss reads as "about 15 min" at its least.
 *
 * Keyed on device ROLE (`utility_feed`, `generator`) and the gateway's keys, never on a name.
 * Pure: reads in, buckets and figures out, every rule tested without a screen.
 */
import type { LatestRow, RangeResponse } from '@/lib/api/types';
import { engineTurning } from './data-centre-analytics';

export type SupplyState = 'grid' | 'generator' | 'none' | 'unknown';

/** Under half the nominal line voltage (400 V) an incomer is dead, not low. */
export const DEAD_LINE_V = 200;
/** Under half the nominal phase voltage (230 V) the controller's mains is dead. */
export const DEAD_PHASE_V = 115;
/** Above this the generator is carrying load, not idling. */
export const GEN_ON_KW = 0.5;

export interface ContinuityIds {
  /** Each grid incomer's line-voltage series (its phases), one array per incomer. */
  incomers: ReadonlyArray<readonly number[]>;
  /** The generator controller's mains phase-voltage series. */
  mains: readonly number[];
  /** The generator's output series. */
  genKw: readonly number[];
  /**
   * Its engine's speed and output frequency, read in the same `max` buckets as `genKw`: the
   * generator carries nothing in a bucket where neither says the engine turned (`engineTurning`),
   * whatever its output register reads. Absent, the register is taken at its word.
   */
  genRun?: { speed: readonly number[]; hz: readonly number[] };
}

export interface SupplyBucket {
  t: number;
  state: SupplyState;
  /** Every witness that reported saw the grid dead; null when none reported. */
  lost: boolean | null;
}

export interface SupplySpan {
  state: SupplyState;
  from: number;
  to: number;
}

export interface GridLoss {
  from: number;
  to: number;
  minutes: number;
  /** The generator carried the site for at least one bucket of it. */
  generator: boolean;
}

export interface Continuity {
  buckets: SupplyBucket[];
  spans: SupplySpan[];
  losses: GridLoss[];
  /** Minutes the generator carried the site, to the bucket. */
  generatorMinutes: number;
  /**
   * The grid has been READ present since this instant: the start of the unbroken run of grid
   * buckets the latest reading ends. That is the last loss's end, the window's first reading, or
   * where readings resumed after a silence.
   */
  onGridSince: number | null;
  /** True when `onGridSince` is the window's first reading. */
  sinceFirstReading: boolean;
  /** True when `onGridSince` is where readings resumed after a silence: before it, nothing is known. */
  sinceReadResumed: boolean;
  /** The end of the latest bucket a witness reported in; null when none did. */
  lastReadAt: number | null;
  /** The latest read bucket was a loss. */
  lostNow: boolean;
}

function valuesAt(range: RangeResponse | undefined): Map<number, Map<number, number>> {
  const out = new Map<number, Map<number, number>>();
  for (const s of range?.series ?? []) {
    const m = new Map<number, number>();
    for (const p of s.points) if (p.v !== null && Number.isFinite(p.v)) m.set(Date.parse(p.t), p.v);
    out.set(s.seriesId, m);
  }
  return out;
}

/** The lowest of a set of series at one bucket; null when none reported. */
function lowest(at: Map<number, Map<number, number>>, ids: readonly number[], t: number): number | null {
  let v: number | null = null;
  for (const id of ids) {
    const x = at.get(id)?.get(t);
    if (x !== undefined) v = v === null ? x : Math.min(v, x);
  }
  return v;
}

function highest(at: Map<number, Map<number, number>>, ids: readonly number[], t: number): number | null {
  let v: number | null = null;
  for (const id of ids) {
    const x = at.get(id)?.get(t);
    if (x !== undefined) v = v === null ? x : Math.max(v, x);
  }
  return v;
}

/**
 * The site's supply, bucket by bucket from `from` to `to`. `minVolts` holds the voltages read with
 * agg `min`, `maxGen` the generator's output read with agg `max`, both in buckets of `bucketMs`.
 */
export function continuity(
  minVolts: RangeResponse | undefined,
  maxGen: RangeResponse | undefined,
  ids: ContinuityIds,
  from: number,
  to: number,
  bucketMs: number,
): Continuity {
  const volts = valuesAt(minVolts);
  const gen = valuesAt(maxGen);
  const buckets: SupplyBucket[] = [];
  for (let t = Math.floor(from / bucketMs) * bucketMs; t < to; t += bucketMs) {
    const witnesses: boolean[] = [];
    for (const inc of ids.incomers) {
      const v = lowest(volts, inc, t);
      if (v !== null) witnesses.push(v < DEAD_LINE_V);
    }
    const mains = lowest(volts, ids.mains, t);
    if (mains !== null) witnesses.push(mains < DEAD_PHASE_V);
    const kw = highest(gen, ids.genKw, t);
    const lost = witnesses.length === 0 ? null : witnesses.every((d) => d);
    const turning = ids.genRun ? engineTurning(highest(gen, ids.genRun.speed, t), highest(gen, ids.genRun.hz, t)) : null;
    const carrying = kw !== null && kw > GEN_ON_KW && turning !== false;
    const state: SupplyState = carrying ? 'generator' : lost === true ? 'none' : lost === false ? 'grid' : 'unknown';
    buckets.push({ t, state, lost });
  }

  const spans = spansOf(buckets.map((b) => ({ ...b, ms: bucketMs })), to);

  /* A loss is a run of lost buckets; a bucket nobody read ends it — a guess either way is worse. */
  const losses: GridLoss[] = [];
  let open: GridLoss | null = null;
  for (const b of buckets) {
    if (b.lost === true) {
      if (open) {
        open.to = b.t + bucketMs;
        open.generator = open.generator || b.state === 'generator';
      } else {
        open = { from: b.t, to: b.t + bucketMs, minutes: 0, generator: b.state === 'generator' };
        losses.push(open);
      }
    } else open = null;
  }
  for (const l of losses) l.minutes = Math.round((Math.min(l.to, to) - l.from) / 60_000);

  return {
    buckets,
    spans,
    losses,
    generatorMinutes: Math.round((buckets.filter((b) => b.state === 'generator').length * bucketMs) / 60_000),
    ...heldRun(buckets.map((b) => ({ ...b, ms: bucketMs })), to),
  };
}

/** Like buckets merged into stretches, oldest first, the last one ending at `to`. Each bucket is `ms` long. */
export function spansOf(buckets: ReadonlyArray<{ t: number; state: SupplyState; ms: number }>, to: number): SupplySpan[] {
  const spans: SupplySpan[] = [];
  for (const b of buckets) {
    const last = spans[spans.length - 1];
    if (last && last.state === b.state && last.to === b.t) last.to = b.t + b.ms;
    else spans.push({ state: b.state, from: b.t, to: b.t + b.ms });
  }
  if (spans.length > 0) spans[spans.length - 1]!.to = Math.min(spans[spans.length - 1]!.to, to);
  return spans;
}

/**
 * Where the grid has been read present since, how that run began, and the latest reading, from
 * buckets oldest first, each `ms` long (so a read stitched from quarter hours and hours walks too).
 *
 * HELD ONLY AS LONG AS IT WAS READ (Hamzah, 2026-10-20: "never a line across a silence"). This was
 * the last loss's end, or the first reading: NBR's meters were silent from 19 Oct 5 PM to 20 Oct
 * 7:30 AM, and the card would still have read "On grid" across the night nobody saw. The run now
 * starts at the first grid bucket after the latest loss OR silence, and says which.
 */
export function heldRun(
  buckets: ReadonlyArray<{ t: number; lost: boolean | null; ms: number }>,
  to: number,
): Pick<Continuity, 'onGridSince' | 'sinceFirstReading' | 'sinceReadResumed' | 'lastReadAt' | 'lostNow'> {
  let last = buckets.length - 1;
  while (last >= 0 && buckets[last]!.lost === null) last--;
  const lostNow = last >= 0 && buckets[last]!.lost === true;
  let onGridSince: number | null = null;
  let sinceFirstReading = false;
  let sinceReadResumed = false;
  if (last >= 0 && !lostNow) {
    let start = last;
    while (start > 0 && buckets[start - 1]!.lost === false) start--;
    onGridSince = buckets[start]!.t;
    if (start === 0 || buckets[start - 1]!.lost === null) {
      const earlierRead = buckets.slice(0, start).some((b) => b.lost !== null);
      sinceReadResumed = earlierRead;
      sinceFirstReading = !earlierRead;
    }
  }
  const lastReadAt = last >= 0 ? Math.min(buckets[last]!.t + buckets[last]!.ms, to) : null;
  return { onGridSince, sinceFirstReading, sinceReadResumed, lastReadAt, lostNow };
}

/** "19 h", "2 d 4 h", "45 min": how long the grid has held, in the largest two units that matter. */
export function heldWords(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh === 0 ? `${d} d` : `${d} d ${rh} h`;
}

/** The bucket a period is read in: fine enough to place a loss, within the API's limits. */
export function continuityBucket(fromMs: number, toMs: number): { bucket: '15 minutes' | '1 hour' | '1 day'; ms: number } {
  const days = (toMs - fromMs) / 86_400_000;
  if (days <= 8) return { bucket: '15 minutes', ms: 900_000 };
  if (days <= 62) return { bucket: '1 hour', ms: 3_600_000 };
  return { bucket: '1 day', ms: 86_400_000 };
}

/**
 * THE GENERATOR'S OUTPUT WHERE ITS CONTROLLER SENDS NO TOTAL (2026-10-20). A DSE or a Cummins on a
 * telecom site reports its three phases' power (`generator_phase_power`) and no `generator_total_power`,
 * which is the key `useContinuity` reads the generator's output from; without it the generator would
 * never be seen carrying the site. For such a generator its phase rows are read under the total's
 * key: `continuity` takes the highest series in a bucket, so the set carries load when any phase
 * does (above `GEN_ON_KW`), which is what that test asks. A generator that sends a total is left as
 * it is, and nothing is added or summed: the rows keep their own series, values and times.
 */
export function withGeneratorOutput(rows: readonly LatestRow[], devices: ReadonlyArray<{ id: number; role: string | null }>): LatestRow[] {
  const gens = new Set(devices.filter((d) => d.role === 'generator').map((d) => d.id));
  const hasTotal = new Set(rows.filter((r) => gens.has(r.deviceId) && r.baseKey === 'generator_total_power').map((r) => r.deviceId));
  return rows.map((r) => (gens.has(r.deviceId) && !hasTotal.has(r.deviceId) && r.baseKey === 'generator_phase_power' ? { ...r, baseKey: 'generator_total_power' } : r));
}

/** The readings `useContinuity` reads, asked of `/telemetry/latest` by the page that holds the rows. */
export const CONTINUITY_KEYS = ['line_to_line_volts', 'mains_phase_voltages', 'generator_total_power'] as const;

/* The operator's words (OPERATOR-DASHBOARDS.md §3): Mains and DG. */
export const STATE_WORDS: Record<SupplyState, string> = {
  grid: 'Mains',
  generator: 'DG',
  none: 'No source',
  unknown: 'Not read',
};

/** "Oct 15, 7:14 PM" on the site's clock. */
export function when(t: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(t);
}
